import { ReactElement, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { Heading } from '../../../components/Heading';
import { Paragraph } from '../../../components/Paragraph';
import { ChevronLeftIcon, PersonGroupIcon, PhoneIcon, WalletIcon, PencilIcon } from '@navikt/aksel-icons';
import classes from './CustomerDetailPage.module.css';
import { Routes } from '../../../enums/Routes';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { Alert } from '../../../components/Alert/Alert';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { ResourceUsageCard } from '../../../components/ResourceUsageCard/ResourceUsageCard';
import { TabNavigation, type Tab } from '../../../components/TabNavigation/TabNavigation';
import { StatsCardsGrid, type StatsCardConfig } from '../../../components/StatsCard/StatsCard';
import { TextField } from '../../../components/TextField';
import { SelectField } from '../../../components/SelectField/SelectField';
import { Button } from '../../../components/Button/Button';
import { formatNorwegianDate } from '../../../utils/dateFormatters';
import { formatNOK } from '../../../utils/currencyFormatters';
import { useGetPartnerCustomerDetailQuery } from '../../../hooks/queries/useGetPartnerCustomerDetailQuery';
import { useUpdateCustomerPricingMutation } from '../../../hooks/mutations/useUpdateCustomerPricingMutation';
import {
  parseOptionalNumber,
  validateBillingStartDate,
  convertNOKToOre,
  convertOreToNOK,
} from '../../../utils/pricing';

const ERROR_MESSAGE = 'Kunne ikke laste kundedata. Prøv igjen senere.';
const NOT_FOUND_MESSAGE = 'Kunde ikke funnet.';
const UPDATE_SUCCESS_MESSAGE = 'Prisinformasjon oppdatert!';
const UPDATE_ERROR_MESSAGE = 'Kunne ikke oppdatere prisinformasjon. Prøv igjen senere.';

type TabType = 'overview' | 'settings';

type CustomerData = {
  readonly id: string;
  readonly name: string;
  readonly createdAt: Date;
  readonly status: string;
  readonly plan: string;
  readonly licenses: {
    readonly total: number;
    readonly used: number;
  };
  readonly sms: {
    readonly quota: number;
    readonly used: number;
  };
  readonly teams: number;
  readonly users: number;
  readonly cashback: {
    readonly cashbackPercentage: number | null;
    readonly monthlyCashbackNOK: number;
    readonly yearlyCashbackNOK: number;
  };
  readonly pricing: {
    readonly pricePerSeat: number | null;
    readonly billingPeriod: string | null;
    readonly billingStartDate: Date | null;
    readonly contractEndDate: Date | null;
  };
};

// Transform API data to component data format
const transformCustomerData = (
  apiData: ReturnType<typeof useGetPartnerCustomerDetailQuery>['data']
): CustomerData | null => {
  if (!apiData) return null;

  return {
    id: String(apiData.id),
    name: apiData.name,
    createdAt: new Date(apiData.createdAt),
    status: apiData.status,
    plan: apiData.plan,
    licenses: {
      total: apiData.availableSeats || 0,
      used: apiData.seatsUsed,
    },
    sms: {
      quota: apiData.smsQuota || 0,
      used: apiData.smsUsed,
    },
    teams: apiData.teamCount,
    users: apiData.userCount,
    cashback: {
      cashbackPercentage: apiData.cashbackPercentage,
      monthlyCashbackNOK: apiData.monthlyCashbackNOK,
      yearlyCashbackNOK: apiData.yearlyCashbackNOK,
    },
    pricing: {
      pricePerSeat: apiData.pricePerSeat,
      billingPeriod: apiData.billingPeriod,
      billingStartDate: apiData.billingStartDate ? new Date(apiData.billingStartDate) : null,
      contractEndDate: apiData.contractEndDate ? new Date(apiData.contractEndDate) : null,
    },
  };
};

// Tab Configuration
const TABS: readonly Tab<TabType>[] = [
  { id: 'overview', label: 'Oversikt' },
  { id: 'settings', label: 'Innstillinger' },
];

// Pricing Edit Form Schema
const pricingEditSchema = z.object({
  pricePerSeat: z.string().optional(),
  availableSeats: z.string().optional(),
  billingPeriod: z.enum(['', 'monthly', 'yearly']).optional(),
  billingStartDate: z.string().optional(),
  contractEndDate: z.string().optional(),
});

type PricingFormData = z.infer<typeof pricingEditSchema>;

// Pure Functions
const createOverviewStats = (customer: CustomerData): readonly StatsCardConfig[] => [
  {
    title: 'Antall team',
    icon: <PersonGroupIcon />,
    value: customer.teams,
  },
  {
    title: 'Brukere',
    icon: <PersonGroupIcon />,
    value: customer.users,
    meta: 'registrerte',
  },
];

const formatPricePerSeat = (priceInOre: number | null): string => {
  if (priceInOre === null) return 'Ikke satt';
  const priceInNOK = priceInOre / 100;
  return formatNOK(priceInNOK);
};

const formatBillingPeriod = (period: string | null): string => {
  if (!period) return 'Ikke satt';
  return period === 'monthly' ? 'Månedlig' : 'Årlig';
};

const hasPricingConfigured = (pricing: CustomerData['pricing']): boolean => {
  return pricing.pricePerSeat !== null;
};

// Tab Content Components
type OverviewTabProps = {
  readonly customer: CustomerData;
};

const OverviewTab = ({ customer }: OverviewTabProps): ReactElement => {
  const stats = useMemo(() => createOverviewStats(customer), [customer]);

  return (
    <>
      <StatsCardsGrid cards={stats} columns={2} />

      <div className={classes.resourcesSection}>
        <ResourceUsageCard
          title="Lisenser"
          icon={<PersonGroupIcon />}
          resource={customer.licenses}
        />
        <ResourceUsageCard
          title="SMS-kvote"
          icon={<PhoneIcon />}
          resource={{ used: customer.sms.used, total: customer.sms.quota }}
          labels={{ total: 'Totalt kvote', available: 'Gjenstående' }}
        />
        <ContainerCard>
          <ContainerCard.Title icon={<WalletIcon />}>Cashback</ContainerCard.Title>
          <ContainerCard.Body>
            <div className={classes.cashbackContainer}>
              <div className={classes.cashbackItem}>
                <div className={classes.cashbackLabel}>Månedlig</div>
                <div className={classes.cashbackValue}>{formatNOK(customer.cashback.monthlyCashbackNOK)}</div>
              </div>
              <div className={classes.cashbackItem}>
                <div className={classes.cashbackLabel}>Årlig</div>
                <div className={classes.cashbackValue}>{formatNOK(customer.cashback.yearlyCashbackNOK)}</div>
              </div>
            </div>
          </ContainerCard.Body>
        </ContainerCard>
      </div>
    </>
  );
};

type SettingsTabProps = {
  readonly customer: CustomerData;
};

const SettingsTab = ({ customer }: SettingsTabProps): ReactElement => {
  const [isEditingPricing, setIsEditingPricing] = useState(false);
  const [formData, setFormData] = useState<PricingFormData>({
    pricePerSeat: customer.pricing.pricePerSeat ? String(convertOreToNOK(customer.pricing.pricePerSeat)) : '',
    availableSeats: customer.licenses.total ? String(customer.licenses.total) : '',
    billingPeriod: (customer.pricing.billingPeriod as 'monthly' | 'yearly' | '') || '',
    billingStartDate: customer.pricing.billingStartDate ? customer.pricing.billingStartDate.toISOString().split('T')[0] : '',
    contractEndDate: customer.pricing.contractEndDate ? customer.pricing.contractEndDate.toISOString().split('T')[0] : '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof PricingFormData, string>>>({});

  const updateMutation = useUpdateCustomerPricingMutation();

  const handleEdit = () => {
    setIsEditingPricing(true);
  };

  const handleCancel = () => {
    setIsEditingPricing(false);
    setFormData({
      pricePerSeat: customer.pricing.pricePerSeat ? String(convertOreToNOK(customer.pricing.pricePerSeat)) : '',
      availableSeats: customer.licenses.total ? String(customer.licenses.total) : '',
      billingPeriod: (customer.pricing.billingPeriod as 'monthly' | 'yearly' | '') || '',
      billingStartDate: customer.pricing.billingStartDate ? customer.pricing.billingStartDate.toISOString().split('T')[0] : '',
      contractEndDate: customer.pricing.contractEndDate ? customer.pricing.contractEndDate.toISOString().split('T')[0] : '',
    });
    setErrors({});
  };

  const handleSubmit = async () => {
    // Validate
    const dateValidation = validateBillingStartDate(formData.billingStartDate || '');
    if (!dateValidation.isValid) {
      setErrors({ billingStartDate: dateValidation.errorMessage });
      return;
    }

    // Validate contract end date is after billing start date
    if (formData.billingStartDate && formData.contractEndDate) {
      const startDate = new Date(formData.billingStartDate);
      const endDate = new Date(formData.contractEndDate);

      if (endDate <= startDate) {
        setErrors({ contractEndDate: 'Sluttdato må være etter startdato for fakturering' });
        return;
      }
    }

    try {
      const pricePerSeatNOK = parseOptionalNumber(formData.pricePerSeat || '');
      const pricePerSeatOre = pricePerSeatNOK !== null ? convertNOKToOre(pricePerSeatNOK) : null;

      await updateMutation.mutateAsync({
        customerId: Number(customer.id),
        payload: {
          pricePerSeat: pricePerSeatOre,
          availableSeats: parseOptionalNumber(formData.availableSeats || ''),
          billingPeriod: formData.billingPeriod === '' ? null : (formData.billingPeriod as 'monthly' | 'yearly'),
          billingStartDate: formData.billingStartDate || null,
          contractEndDate: formData.contractEndDate || null,
        },
      });

      toast.success(UPDATE_SUCCESS_MESSAGE);
      setIsEditingPricing(false);
    } catch (error) {
      toast.error(UPDATE_ERROR_MESSAGE);
    }
  };

  return (
    <>
      <ContainerCard>
        <ContainerCard.Title>Kundeinnstillinger</ContainerCard.Title>
        <ContainerCard.Body>
          <div className={classes.infoGrid}>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Kundenavn:</span>
              <span className={classes.infoValue}>{customer.name}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Plan:</span>
              <span className={classes.infoValue}>{customer.plan}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Status:</span>
              <span className={classes.infoValue}>
                <span className={classes.statusBadge}>
                  {customer.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                </span>
              </span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Opprettet:</span>
              <span className={classes.infoValue}>{formatNorwegianDate(customer.createdAt)}</span>
            </div>
          </div>
        </ContainerCard.Body>
      </ContainerCard>

      <ContainerCard>
        <ContainerCard.Title>
          Prisinformasjon
          {!isEditingPricing && (
            <Button
              variant="tertiary"
              size="sm"
              onClick={handleEdit}
              style={{ marginLeft: 'auto' }}
            >
              <PencilIcon aria-hidden fontSize="18px" />
              Rediger
            </Button>
          )}
        </ContainerCard.Title>
        <ContainerCard.Body>
          {isEditingPricing ? (
            <div className={classes.pricingForm}>
              <TextField
                label="Pris per lisens i NOK (valgfritt)"
                placeholder="F.eks. 299"
                type="number"
                value={formData.pricePerSeat}
                onChange={(e) => setFormData({ ...formData, pricePerSeat: e.target.value })}
                error={errors.pricePerSeat}
              />
              <TextField
                label="Antall lisenser (valgfritt)"
                placeholder="Antall lisenser, eller la stå tom for ubegrenset"
                type="number"
                value={formData.availableSeats}
                onChange={(e) => setFormData({ ...formData, availableSeats: e.target.value })}
                error={errors.availableSeats}
              />
              <SelectField
                label="Faktureringsperiode (valgfritt)"
                value={formData.billingPeriod || ''}
                onChange={(e) => setFormData({ ...formData, billingPeriod: e.target.value as '' | 'monthly' | 'yearly' })}
                options={[
                  { value: '', label: 'Velg faktureringsperiode' },
                  { value: 'monthly', label: 'Månedlig' },
                  { value: 'yearly', label: 'Årlig' },
                ]}
              />
              <TextField
                label="Startdato for fakturering (valgfritt)"
                type="date"
                value={formData.billingStartDate}
                onChange={(e) => setFormData({ ...formData, billingStartDate: e.target.value })}
                error={errors.billingStartDate}
              />
              <TextField
                label="Sluttdato for avtale (valgfritt)"
                type="date"
                value={formData.contractEndDate}
                onChange={(e) => setFormData({ ...formData, contractEndDate: e.target.value })}
                error={errors.contractEndDate}
              />
              <div className={classes.formActions}>
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleSubmit}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? 'Lagrer...' : 'Lagre endringer'}
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={handleCancel}
                  disabled={updateMutation.isPending}
                >
                  Avbryt
                </Button>
              </div>
            </div>
          ) : (
            <>
              {hasPricingConfigured(customer.pricing) ? (
                <div className={classes.infoGrid}>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Pris per lisens:</span>
                    <span className={classes.infoValue}>{formatPricePerSeat(customer.pricing.pricePerSeat)}</span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Antall lisenser:</span>
                    <span className={classes.infoValue}>{customer.licenses.total || 'Ubegrenset'}</span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Faktureringsperiode:</span>
                    <span className={classes.infoValue}>{formatBillingPeriod(customer.pricing.billingPeriod)}</span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Fakturering starter:</span>
                    <span className={classes.infoValue}>
                      {customer.pricing.billingStartDate
                        ? formatNorwegianDate(customer.pricing.billingStartDate)
                        : 'Ikke satt'}
                    </span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Avtale slutter:</span>
                    <span className={classes.infoValue}>
                      {customer.pricing.contractEndDate
                        ? formatNorwegianDate(customer.pricing.contractEndDate)
                        : 'Løper evig'}
                    </span>
                  </div>
                </div>
              ) : (
                <Paragraph>
                  Ingen prisinformasjon er registrert for denne kunden. Dette betyr at kunden ikke blir fakturert,
                  og det genereres ingen cashback.
                </Paragraph>
              )}
            </>
          )}
        </ContainerCard.Body>
      </ContainerCard>

      {hasPricingConfigured(customer.pricing) && (
        <ContainerCard>
          <ContainerCard.Title>Estimert cashback for denne kunden</ContainerCard.Title>
          <ContainerCard.Body>
            <Paragraph size="sm" style={{ marginBottom: '1rem', color: 'var(--a-text-subtle)' }}>
              Estimert cashback er partneren sin andel av inntekten fra denne kunden, basert på avtalt cashback-prosent.
            </Paragraph>
            <div className={classes.cashbackBreakdown}>
              <div className={classes.breakdownRow}>
                <span className={classes.breakdownLabel}>Pris per lisens</span>
                <span className={classes.breakdownValue}>{formatPricePerSeat(customer.pricing.pricePerSeat)}</span>
              </div>
              <div className={classes.breakdownRow}>
                <span className={classes.breakdownLabel}>Antall lisenser</span>
                <span className={classes.breakdownValue}>{customer.licenses.total}</span>
              </div>

              <div className={classes.breakdownDivider} />

              <div className={classes.breakdownRow}>
                <span className={classes.breakdownLabel}><strong>Total månedssum til sluttkunde</strong></span>
                <span className={classes.breakdownValue}>
                  <strong>
                    {customer.pricing.pricePerSeat && customer.licenses.total
                      ? formatNOK((customer.pricing.pricePerSeat / 100) * customer.licenses.total)
                      : formatNOK(0)}
                  </strong>
                </span>
              </div>

              <div className={classes.breakdownHighlight}>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Cashback-prosent til partner</span>
                  <span className={classes.breakdownValue}>
                    {customer.cashback.cashbackPercentage !== null
                      ? `${(customer.cashback.cashbackPercentage / 100).toFixed(1)}%`
                      : 'Ikke satt'}
                  </span>
                </div>
              </div>

              <div className={classes.breakdownHighlight}>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Månedlig cashback til partner</span>
                  <span className={classes.breakdownValue}>{formatNOK(customer.cashback.monthlyCashbackNOK)}</span>
                </div>
              </div>

              <div className={classes.breakdownHighlight}>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Årlig cashback til partner</span>
                  <span className={classes.breakdownValue}>{formatNOK(customer.cashback.yearlyCashbackNOK)}</span>
                </div>
              </div>
            </div>
          </ContainerCard.Body>
        </ContainerCard>
      )}
    </>
  );
};

// Main Component
export const CustomerDetailPage = (): ReactElement => {
  const { customerId } = useParams<{ customerId: string }>();
  const { data: apiData, isLoading, error } = useGetPartnerCustomerDetailQuery(Number(customerId));
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  if (isLoading) {
    return <GhostLoader />;
  }

  if (error) {
    return (
      <div className={classes.container}>
        <Alert variant="danger" size="md">
          <Paragraph>{ERROR_MESSAGE}</Paragraph>
        </Alert>
      </div>
    );
  }

  const customerDetail = transformCustomerData(apiData);

  if (!customerDetail) {
    return (
      <div className={classes.container}>
        <Alert variant="warning" size="md">
          <Paragraph>{NOT_FOUND_MESSAGE}</Paragraph>
        </Alert>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Link to={Routes.PartnerAdminCustomers} className={classes.backLink}>
        <ChevronLeftIcon aria-hidden="true" fontSize="20px" />
        <span>Tilbake til kunder</span>
      </Link>

      <div className={classes.header}>
        <Heading as="h1">{customerDetail.name}</Heading>
        <Paragraph size="lg">
          Kunde siden {formatNorwegianDate(customerDetail.createdAt)} · <strong>{customerDetail.plan}</strong> plan
        </Paragraph>
      </div>

      <TabNavigation tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />

      <div className={classes.tabContent} role="tabpanel" id={`tabpanel-${activeTab}`}>
        {activeTab === 'overview' && <OverviewTab customer={customerDetail} />}
        {activeTab === 'settings' && <SettingsTab customer={customerDetail} />}
      </div>
    </div>
  );
};
